/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useEffect, useMemo, useState} from 'react';
import SystemService from "../../../models/systemServiceModel";
import {capitalize, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import SystemServices from "./systemServices";
import {onSetCrumbs} from "../../../util/events";
import {toggleAllServices} from "../../../util/requestSender";
import {SystemServiceName} from "../../../util/interfaces";
import {ADMIN_ROUTE, ADMIN_SERVICES_ROUTE} from "../../../util/constants";

export default function SystemServicesController() {
    const [services, setServices] = useState<SystemService[]>([]);
    const [loading, setLoading] = useState(true);
    const someActive = useMemo(() => {
        return !!services.find((service: SystemService) => service.name !== SystemServiceName.EMAIL_SEQUENCE && service.active);
    }, [services]);

    function onToggleAll() {
        const label = someActive ? 'uit' : 'in';
        showDialog(`Weet je zeker dat je alle services ${label} wilt schakelen?`, `Alle services ${label}schakelen`,
            ['Nee', {label: 'Ja', onClick: onConfirmToggleAll, primary: false}]);
    }

    function onConfirmToggleAll() {
        setLoading(true);
        toggleAllServices({
            data: {toggled: !someActive}, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const label = someActive ? 'uit' : 'in';
                    setServices(data.services.map((service: any) => new SystemService(service)));
                    showSnackbar(`Alle services zijn ${label}geschakeld`);
                } else showDialog(data.message ?? 'Het wijzigen van de services is niet gelukt.');
            }
        });
    }

    function onToggleService(service: SystemService) {
        return function () {
            const action = service.active ? 'uitschakelen' : 'inschakelen';
            showDialog(`Weet je zeker dat je deze service wilt ${action}?`, 'Bevestig', [{
                label: 'Nee',
                primary: false
            }, {label: 'Ja', onClick: onConfirmToggleService(service)}]);
        }
    }

    function onConfirmToggleService(service: SystemService) {
        return function () {
            setLoading(true);
            service.toggle((service: SystemService, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    replaceService(service);
                    const action = service.active ? 'ingeschakeld' : 'uitgeschakeld';
                    showSnackbar(`De service is ${action}`);
                } else {
                    const action = service.active ? 'uitschakelen' : 'inschakelen';
                    showDialog(data.message || `Het ${action} van de service is niet gelukt.`, `${capitalize(action)} mislukt`);
                }
            });
        }
    }

    function replaceService(service: SystemService) {
        const _services = services.slice();
        for (let i = 0; i < _services.length; i++) {
            if (_services[i].id === service.id) {
                _services.splice(i, 1, service);
                break;
            }
        }
        setServices(_services);
    }

    function loadServices() {
        SystemService.getAll((services: SystemService[]) => {
            setLoading(false);
            setServices(services);
        });
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Admin', route: ADMIN_ROUTE}, {label: 'Services', route: ADMIN_SERVICES_ROUTE}]);
        loadServices();
    }, []);

    return (
        <SystemServices onToggleService={onToggleService} onToggleAll={onToggleAll} services={services}
                        loading={loading} someActive={someActive}/>
    );
}